import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Box,
  FormHelperText,
  Chip,
  Checkbox,
  IconButton,
  InputAdornment,
  DialogContentText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';

const AddBrand = ({ open, onClose, onBrandAdded, initialBrand }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    categories: []
  });
  const { data: config } = useSelector((state) => state.config);

  useEffect(() => {
    if (initialBrand) {
      const initialCategories = initialBrand.categories ? initialBrand.categories.map(cat => cat._id || cat) : [];
      setFormData({
        name: initialBrand.name || '',
        icon: initialBrand.icon || '',
        categories: initialCategories
      });
      setSelectedCategories(initialCategories);
    } else {
      setFormData({
        name: '',
        icon: '',
        categories: []
      });
      setSelectedCategories([]);
    }
    setError('');
  }, [initialBrand, open]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiClient.get(`${config.API_BASE_URL}/api/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    if (open) {
      fetchCategories();
    }
  }, [open, config.API_BASE_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenCategoryDialog = () => {
    setCategoryDialogOpen(true);
  };

  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
  };

  const handleToggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      const newSelected = prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
      
      setFormData(prev => ({
        ...prev,
        categories: newSelected
      }));
      
      return newSelected;
    });
  };

  const handleRemoveCategory = (categoryId) => {
    setSelectedCategories(prev => {
      const newSelected = prev.filter(id => id !== categoryId);
      setFormData(prev => ({
        ...prev,
        categories: newSelected
      }));
      return newSelected;
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Brand name is required');
      return false;
    }
    if (!formData.icon) {
      setError('Brand icon is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    
    try {
      let response;
      
      if (initialBrand) {
        // Update existing brand
        response = await apiClient.put(
          `${config.API_BASE_URL}/api/brands/${initialBrand._id}`,
          formData
        );
      } else {
        // Create new brand
        response = await apiClient.post(
          `${config.API_BASE_URL}/api/brands`,
          formData
        );
      }
      
      onBrandAdded(response.data);
      onClose();
    } catch (error) {
      console.error('Error saving brand:', error);
      setError(error.response?.data?.message || 'Failed to save brand');
    } finally {
      setLoading(false);
    }
  };

  // Render the category selection dialog
  const renderCategoryDialog = () => (
    <Dialog 
      open={categoryDialogOpen} 
      onClose={handleCloseCategoryDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Select Categories</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Select one or more categories for this brand
        </DialogContentText>
        
        <Box sx={{ mt: 2, maxHeight: 400, overflow: 'auto' }}>
          {categories.map((category) => (
            <Box 
              key={category._id}
              sx={{
                p: 1,
                borderBottom: '1px solid #eee',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                backgroundColor: selectedCategories.includes(category._id) 
                  ? 'action.selected' 
                  : 'inherit'
              }}
              onClick={() => handleToggleCategory(category._id)}
            >
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={selectedCategories.includes(category._id)}
                  tabIndex={-1}
                  disableRipple
                />
                <Box ml={1}>
                  <Typography variant="body1">{category.name}</Typography>
                  {category.description && (
                    <Typography variant="caption" color="textSecondary">
                      {category.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseCategoryDialog} color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {initialBrand ? 'Edit Brand' : 'Add New Brand'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Typography color="error" variant="body2" gutterBottom>
                {error}
              </Typography>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Brand Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Icon URL"
                  name="icon"
                  value={formData.icon}
                  onChange={handleChange}
                  margin="normal"
                  required
                  helperText="Enter the URL of the brand's icon"
                />
                {formData.icon && (
                  <Box mt={1} textAlign="center">
                    <Typography variant="caption">Preview:</Typography>
                    <Box mt={1}>
                      <img 
                        src={formData.icon} 
                        alt="Brand Preview" 
                        style={{ 
                          maxWidth: '100px', 
                          maxHeight: '100px',
                          objectFit: 'contain'
                        }} 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/100?text=Invalid+URL';
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  {/* <InputLabel id="categories-label">Categories</InputLabel> */}
                  
                  <Box sx={{ 
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '40px'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 1,
                      alignItems: 'center'
                    }}>
                      {selectedCategories.map(catId => {
                        const category = categories.find(c => c._id === catId);
                        return category ? (
                          <Chip
                            key={catId}
                            label={category.name}
                            onDelete={(e) => {
                              e.stopPropagation();
                              handleRemoveCategory(catId);
                            }}
                            size="small"
                            sx={{ 
                              maxWidth: '200px',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                pr: 0.5
                              },
                              '&:hover': {
                                bgcolor: 'action.hover'
                              }
                            }}
                            deleteIcon={
                              <CloseIcon 
                                fontSize="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveCategory(catId);
                                }}
                              />
                            }
                          />
                        ) : null;
                      })}
                      <Chip
                        icon={<AddIcon />}
                        label="Add Categories"
                        onClick={handleOpenCategoryDialog}
                        variant="outlined"
                        sx={{
                          borderStyle: 'dashed',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            cursor: 'pointer'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={onClose} color="primary">
              Cancel
            </Button>
            <Button 
              type="submit" 
              color="primary" 
              variant="contained"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : initialBrand ? (
                'Update Brand'
              ) : (
                'Add Brand'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {renderCategoryDialog()}
    </>
  );
};

export default AddBrand;
